<script setup>
// NOTICE: KAPTION-INTEGRATION
// embedded-channel.DASHBOARD.1, embedded-channel.DASHBOARD.2 — create an Embedded inbox: the web widget
// channel, the inbox and its embedded_channels row in one server transaction.
// The website URL is optional (the chat may live only inside an app).
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { KaptionEmbeddedAPI } from 'dashboard/api/kaptionEmbedded';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const form = reactive({
  name: '',
  websiteUrl: '',
  widgetColor: '#009CE0',
  welcomeTitle: '',
  welcomeTagline: '',
  identityMode: 'verified',
});
const isCreating = ref(false);

const identityModes = computed(() => [
  {
    value: 'verified',
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.VERIFIED'),
    help: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.VERIFIED_HELP'),
  },
  {
    value: 'open',
    label: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.OPEN'),
    help: t('INBOX_MGMT.EMBEDDED.IDENTITY_MODES.OPEN_HELP'),
  },
]);

const canSubmit = computed(() => !!form.name.trim() && !isCreating.value);

const payload = () => ({
  name: form.name.trim(),
  website_url: form.websiteUrl.trim() || null,
  widget_color: form.widgetColor,
  welcome_title: form.welcomeTitle.trim(),
  welcome_tagline: form.welcomeTagline.trim(),
  identity_mode: form.identityMode,
});

const createInbox = async () => {
  if (!canSubmit.value) return;
  isCreating.value = true;
  try {
    const accountId = store.getters.getCurrentAccountId;
    const { data: inbox } = await KaptionEmbeddedAPI.createInbox(
      accountId,
      payload()
    );
    await store.dispatch('inboxes/get');
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        accountId: route.params.accountId,
        page: 'new',
        inbox_id: inbox.id,
      },
    });
  } catch (error) {
    const data = error?.response?.data;
    useAlert(
      data?.error ||
        data?.message ||
        data?.errors?.join?.(', ') ||
        t('INBOX_MGMT.EMBEDDED.CREATE.ERROR')
    );
  } finally {
    isCreating.value = false;
  }
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="t('INBOX_MGMT.EMBEDDED.CREATE.TITLE')"
      :header-content="t('INBOX_MGMT.EMBEDDED.CREATE.DESC')"
    />
    <form class="flex flex-col gap-4 max-w-xl" @submit.prevent="createInbox">
      <Input
        v-model="form.name"
        data-testid="embedded-name"
        :label="t('INBOX_MGMT.EMBEDDED.CREATE.NAME_LABEL')"
        :placeholder="t('INBOX_MGMT.EMBEDDED.CREATE.NAME_PLACEHOLDER')"
      />
      <Input
        v-model="form.websiteUrl"
        data-testid="embedded-website-url"
        :label="t('INBOX_MGMT.EMBEDDED.CREATE.WEBSITE_URL_LABEL')"
        :placeholder="t('INBOX_MGMT.EMBEDDED.CREATE.WEBSITE_URL_PLACEHOLDER')"
      />
      <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.EMBEDDED.CREATE.WIDGET_COLOR') }}
        <woot-color-picker v-model="form.widgetColor" />
      </label>
      <Input
        v-model="form.welcomeTitle"
        :label="t('INBOX_MGMT.EMBEDDED.CREATE.WELCOME_TITLE_LABEL')"
        :placeholder="t('INBOX_MGMT.EMBEDDED.CREATE.WELCOME_TITLE_PLACEHOLDER')"
      />
      <Input
        v-model="form.welcomeTagline"
        :label="t('INBOX_MGMT.EMBEDDED.CREATE.WELCOME_TAGLINE_LABEL')"
        :placeholder="
          t('INBOX_MGMT.EMBEDDED.CREATE.WELCOME_TAGLINE_PLACEHOLDER')
        "
      />
      <fieldset class="flex flex-col gap-2">
        <legend class="mb-1 text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EMBEDDED.CREATE.IDENTITY_LABEL') }}
        </legend>
        <label
          v-for="mode in identityModes"
          :key="mode.value"
          class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
          :class="
            form.identityMode === mode.value
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak'
          "
        >
          <input
            v-model="form.identityMode"
            type="radio"
            name="embedded-identity-mode"
            class="mt-1"
            :value="mode.value"
            :data-testid="`identity-${mode.value}`"
          />
          <span class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ mode.label }}
            </span>
            <span class="text-sm text-n-slate-11">{{ mode.help }}</span>
          </span>
        </label>
      </fieldset>
      <div>
        <NextButton
          type="submit"
          :disabled="!canSubmit"
          :is-loading="isCreating"
          :label="t('INBOX_MGMT.EMBEDDED.CREATE.SUBMIT')"
        />
      </div>
    </form>
  </div>
</template>
